import AddIcon from "@mui/icons-material/Add";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  Link,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AppDialog from "components/common/AppDialog";
import ConfirmDialog from "components/common/ConfirmDialog";
import CustomButton from "components/common/CustomButton";
import { usePlatformRole } from "features/integrationsBeta/usePlatformRole";
import {
  useDeleteWebhookTriggerMutation,
  useGetWebhookTriggersQuery,
  useRedriveWebhookDlqMutation,
  useRevealWebhookSecretMutation,
  useRotateWebhookSecretMutation,
  useRotateWebhookTriggerMutation,
  type WebhookTrigger,
} from "features/integrationsBeta/webhookTriggers";
import { pushNotification } from "features/notificationSlice";
import { useAppDispatch } from "features/store";
import { useState } from "react";

import { usePendingChanges } from "./pending/PendingChangesContext";
import SectionCard from "./SectionCard";
import WebhookSetupReveal from "./WebhookSetupReveal";
import WebhookTriggerCreateDialog from "./WebhookTriggerCreateDialog";

interface Props {
  integrationId: number;
  // Rendered on a schedule-mode integration that still has trigger rows:
  // warn + delete-only, so orphans can be cleaned up.
  orphanMode?: boolean;
}

interface SecretView {
  secretKey: string;
  oneTime: boolean;
}

/**
 * Operational view of an integration's inbound webhook triggers: the URL to
 * paste into the provider UI, the last Verify-URL code, an active toggle, the
 * Secret-Key (reveal/rotate), create/delete, and an operator DLQ redrive. The
 * secret is auto-generated on create and managed here - the operator never
 * touches the underlying connection.
 */
export default function WebhookTriggerPanel({
  integrationId,
  orphanMode = false,
}: Props) {
  const dispatch = useAppDispatch();
  const { canEdit, canReveal, canRun } = usePlatformRole();
  const { data: triggers, isLoading } =
    useGetWebhookTriggersQuery(integrationId);
  const { stage, getPending } = usePendingChanges();
  const [redrive, { isLoading: redriving }] = useRedriveWebhookDlqMutation();
  const [rotate, { isLoading: rotating }] = useRotateWebhookTriggerMutation();
  const [revealSecret, { isLoading: revealing }] =
    useRevealWebhookSecretMutation();
  const [rotateSecret, { isLoading: rotatingSecret }] =
    useRotateWebhookSecretMutation();
  const [deleteTrigger, { isLoading: deleting }] =
    useDeleteWebhookTriggerMutation();
  const [rotateId, setRotateId] = useState<number | null>(null);
  const [rotateSecretId, setRotateSecretId] = useState<number | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState<WebhookTrigger | null>(null);
  const [renameDraft, setRenameDraft] = useState("");
  const [secretView, setSecretView] = useState<SecretView | null>(null);

  const triggerEntityLabel = (t: WebhookTrigger) =>
    `Webhook trigger "${t.name || "Unnamed"}"`;
  const stagedName = (t: WebhookTrigger) =>
    (getPending("webhookTrigger", t.id, "name")?.newValue as
      | string
      | undefined) ?? t.name;
  const stagedActive = (t: WebhookTrigger) =>
    (getPending("webhookTrigger", t.id, "is_active")?.newValue as
      | boolean
      | undefined) ?? t.is_active;

  const copy = (text: string) => {
    navigator.clipboard?.writeText(text);
    dispatch(pushNotification({ severity: "success", message: "Copied." }));
  };

  const confirmRotate = async () => {
    if (rotateId == null) return;
    try {
      await rotate({ id: rotateId, integration: integrationId }).unwrap();
      dispatch(
        pushNotification({
          severity: "success",
          message:
            "Webhook URL regenerated - update it in the provider UI and re-verify.",
        }),
      );
    } catch {
      dispatch(
        pushNotification({
          severity: "error",
          message: "Could not regenerate the URL.",
        }),
      );
    } finally {
      setRotateId(null);
    }
  };

  const handleReveal = async (id: number) => {
    try {
      const { secret_key } = await revealSecret({ id }).unwrap();
      setSecretView({ secretKey: secret_key, oneTime: false });
    } catch {
      dispatch(
        pushNotification({
          severity: "error",
          message: "Could not reveal the secret.",
        }),
      );
    }
  };

  const confirmRotateSecret = async () => {
    if (rotateSecretId == null) return;
    try {
      const { secret_key } = await rotateSecret({
        id: rotateSecretId,
      }).unwrap();
      setSecretView({ secretKey: secret_key, oneTime: true });
    } catch {
      dispatch(
        pushNotification({
          severity: "error",
          message: "Could not rotate the secret.",
        }),
      );
    } finally {
      setRotateSecretId(null);
    }
  };

  const confirmDelete = async () => {
    if (deleteId == null) return;
    try {
      await deleteTrigger({
        id: deleteId,
        integration: integrationId,
      }).unwrap();
      dispatch(
        pushNotification({
          severity: "success",
          message: "Webhook trigger deleted.",
        }),
      );
    } catch {
      dispatch(
        pushNotification({
          severity: "error",
          message: "Could not delete the trigger.",
        }),
      );
    } finally {
      setDeleteId(null);
    }
  };

  const openRename = (trigger: WebhookTrigger) => {
    setRenameTarget(trigger);
    setRenameDraft(stagedName(trigger));
  };

  const applyRename = () => {
    if (renameTarget == null) return;
    stage({
      entityType: "webhookTrigger",
      entityId: renameTarget.id,
      section: "webhooks",
      entityLabel: triggerEntityLabel(renameTarget),
      field: "name",
      fieldLabel: "Name",
      valueKind: "text",
      oldValue: renameTarget.name,
      newValue: renameDraft.trim(),
    });
    setRenameTarget(null);
  };

  const toggle = (t: WebhookTrigger, is_active: boolean) =>
    stage({
      entityType: "webhookTrigger",
      entityId: t.id,
      section: "webhooks",
      entityLabel: triggerEntityLabel(t),
      field: "is_active",
      fieldLabel: "Active",
      valueKind: "active",
      oldValue: t.is_active,
      newValue: is_active,
    });

  const handleRedrive = async () => {
    try {
      const r = await redrive().unwrap();
      dispatch(
        pushNotification({
          severity: "success",
          message: `DLQ redrive started (${r.task_handle}).`,
        }),
      );
    } catch {
      dispatch(
        pushNotification({
          severity: "error",
          message: "Could not start the DLQ redrive.",
        }),
      );
    }
  };

  const canWrite = canEdit && !orphanMode;
  const canRedrive = canRun && !orphanMode;

  return (
    <SectionCard
      title="Webhook Triggers"
      action={
        canWrite ? (
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
          >
            Add webhook trigger
          </Button>
        ) : undefined
      }
    >
      {orphanMode && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          This integration is schedule-driven (no step consumes a webhook
          payload), but webhook triggers still exist. They will not process
          events - delete them.
        </Alert>
      )}

      {isLoading ? (
        <Typography variant="body2" color="text.secondary">
          Loading...
        </Typography>
      ) : !triggers?.length ? (
        <Typography variant="body2" color="text.secondary">
          No webhook triggers are configured for this integration.
        </Typography>
      ) : (
        <Stack spacing={2}>
          {triggers.map((t) => (
            <Box
              key={t.id}
              sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2 }}
            >
              <Stack spacing={1}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <FormControlLabel
                    sx={{ mr: 0.5 }}
                    control={
                      <Switch
                        checked={stagedActive(t)}
                        disabled={!canWrite}
                        onChange={(_, c) => toggle(t, c)}
                      />
                    }
                    label={stagedActive(t) ? "Active" : "Inactive"}
                  />
                  <Chip size="small" label={t.provider} />
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    noWrap
                    sx={{
                      minWidth: 0,
                      color: stagedName(t) ? "text.primary" : "text.secondary",
                    }}
                  >
                    {stagedName(t) || "Unnamed trigger"}
                  </Typography>
                  {canWrite && (
                    <Tooltip title="Rename trigger">
                      <IconButton
                        size="small"
                        onClick={() => openRename(t)}
                        aria-label="Rename trigger"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Box sx={{ flexGrow: 1 }} />
                  {canEdit && (
                    <Tooltip title="Delete trigger">
                      <IconButton
                        size="small"
                        onClick={() => setDeleteId(t.id)}
                        aria-label="Delete trigger"
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <TextField
                    size="small"
                    label="Webhook URL (paste into the provider UI)"
                    value={t.webhook_url}
                    fullWidth
                    InputProps={{ readOnly: true }}
                  />
                  <Tooltip title="Copy URL">
                    <IconButton
                      size="small"
                      onClick={() => copy(t.webhook_url)}
                      aria-label="Copy URL"
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {canWrite && (
                    <Tooltip title="Regenerate URL">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => setRotateId(t.id)}
                          disabled={rotating}
                          aria-label="Regenerate URL"
                        >
                          <AutorenewIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Box>
                {/* Secret values are Contribute, like the credentials of the
                    Integrations page; rotation stays Manage. */}
                {canReveal && (
                  <Typography variant="caption" color="text.secondary">
                    Secret-Key:{" "}
                    <Link
                      component="button"
                      type="button"
                      variant="caption"
                      disabled={revealing}
                      onClick={() => handleReveal(t.id)}
                    >
                      Reveal
                    </Link>
                    {canWrite && (
                      <>
                        {" · "}
                        <Link
                          component="button"
                          type="button"
                          variant="caption"
                          disabled={rotatingSecret}
                          onClick={() => setRotateSecretId(t.id)}
                        >
                          Rotate
                        </Link>
                      </>
                    )}
                  </Typography>
                )}
                {t.verification_code ? (
                  <Alert severity="info">
                    Verify URL code: <strong>{t.verification_code}</strong> -
                    enter it in the provider webhook UI to finish setup.
                  </Alert>
                ) : null}
              </Stack>
            </Box>
          ))}
          {canRedrive && (
            <Box>
              <CustomButton
                fullWidth={false}
                onClick={handleRedrive}
                disabled={redriving}
              >
                Redrive DLQ
              </CustomButton>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mt: 0.5 }}
              >
                Move failed webhook events from the DLQ back to the queue after
                fixing the root cause.
              </Typography>
            </Box>
          )}
        </Stack>
      )}

      <ConfirmDialog
        open={rotateId != null}
        title="Regenerate webhook URL?"
        message={
          "The current URL stops working immediately. Paste the new URL into " +
          "the provider (Paycom) and re-verify it to finish setup."
        }
        confirmLabel="Regenerate"
        onConfirm={confirmRotate}
        onCancel={() => setRotateId(null)}
        loading={rotating}
      />

      <ConfirmDialog
        open={rotateSecretId != null}
        title="Rotate Secret-Key?"
        message={
          "A new Secret-Key is generated and shown once. The current one stops " +
          "working immediately - update it in the provider (Paycom) webhook UI."
        }
        confirmLabel="Rotate"
        onConfirm={confirmRotateSecret}
        onCancel={() => setRotateSecretId(null)}
        loading={rotatingSecret}
      />

      <ConfirmDialog
        open={deleteId != null}
        title="Delete webhook trigger?"
        message="The webhook URL stops working immediately and the provider will get errors until it is removed there too."
        confirmLabel="Delete"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteId(null)}
        loading={deleting}
      />

      <AppDialog
        open={renameTarget != null}
        onClose={() => setRenameTarget(null)}
        title="Rename webhook trigger"
        maxWidth="xs"
        showCloseIcon
        actions={
          <>
            <CustomButton
              fullWidth={false}
              onClick={() => setRenameTarget(null)}
            >
              Cancel
            </CustomButton>
            <CustomButton fullWidth={false} onClick={applyRename}>
              Apply
            </CustomButton>
          </>
        }
      >
        <TextField
          autoFocus
          fullWidth
          size="small"
          label="Name"
          value={renameDraft}
          onChange={(e) => setRenameDraft(e.target.value)}
          helperText="Optional label to tell sibling triggers apart."
          sx={{ mt: 1 }}
        />
      </AppDialog>

      <AppDialog
        open={secretView != null}
        onClose={() => setSecretView(null)}
        title="Webhook Secret-Key"
        maxWidth="sm"
        showCloseIcon
        actions={
          <CustomButton fullWidth={false} onClick={() => setSecretView(null)}>
            Done
          </CustomButton>
        }
      >
        {secretView && (
          <Box sx={{ mt: 1 }}>
            <WebhookSetupReveal
              secretKey={secretView.secretKey}
              oneTime={secretView.oneTime}
            />
          </Box>
        )}
      </AppDialog>

      {createOpen && (
        <WebhookTriggerCreateDialog
          open
          onClose={() => setCreateOpen(false)}
          integrationId={integrationId}
        />
      )}
    </SectionCard>
  );
}
